import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '@afsa/shared'
import ViewAllButton from '@/components/ui/ViewAllButton'
import { type WebPageSection } from '@/lib/api'
import { useStrategicPillarHighlights, usePublishedStrategicPillars } from '@/lib/api/hooks/strategic-pillar'
import { useProgrammeHighlights, usePublishedProgrammes } from '@/lib/api/hooks/programme'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer } from '@/lib/motion'
import { parseHtmlString, stripHtml } from '@/utils/helpers/html'
import Heading from '@/components/ui/Typography/Heading'
import { Text } from '@/components/ui/Typography'


interface PriorityCardProps {
  title: string
  description: string
  image: string
  onClick?: () => void
}

const PriorityCard = ({ title, description, image, onClick }: PriorityCardProps) => {
  const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760297/333506ab901b83e4fee5ac8a037c9429a71fb1f8_1_fgkekh.png"

  return (
    <motion.article
      className="bg-duskySky-100 overflow-hidden hover:bg-wine-200 hover:text-white transition-all duration-300 h-full group cursor-pointer"
      whileHover={{ y: -5 }}
      transition={{ type: 'spring', stiffness: 280, damping: 24 }}
      onClick={onClick}
    >
      <div className="relative overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-48 object-cover md:group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            // Fallback to default image if the image fails to load
            if (e.currentTarget.src !== defaultImage) {
              e.currentTarget.src = defaultImage
            }
          }}
        />
        <div className=" hidden md:block absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      <div className=" p-3 md:p-6 ">
        <Heading level={4} className="mb-3 group-hover:text-white transition-colors duration-200">
          {title}
        </Heading>
        <Text className="  group-hover:text-white  line-clamp-3">
          {stripHtml(description)}
        </Text>
      </div>
    </motion.article>
  )
}

interface PrioritiesSectionProps {
  className?: string;
  data: {
    [key: string]: WebPageSection
  }
}

const PrioritiesSection = ({ className, data }: PrioritiesSectionProps) => {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'strategic' | 'programmes'>('strategic')

  // Default image for fallback
  const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760297/333506ab901b83e4fee5ac8a037c9429a71fb1f8_1_fgkekh.png"

  // Fetch strategic pillar highlights first, fallback to all published
  const { data: strategicHighlightsData } = useStrategicPillarHighlights()
  const { data: allStrategicPillarsData, isLoading: isAllStrategicLoading } = usePublishedStrategicPillars(
    { page: 1, size: 10 },
    {}
  )

  // Fetch programme highlights first, fallback to all published
  const { data: programmeHighlightsData } = useProgrammeHighlights()
  const { data: allProgrammesData, isLoading: isAllProgrammesLoading } = usePublishedProgrammes(
    { page: 1, size: 4 }
  )

  // Use highlights if available, otherwise use all published
  const strategicPillarsData = strategicHighlightsData?.data && strategicHighlightsData.data.length > 0
    ? { data: { data: strategicHighlightsData.data } }
    : allStrategicPillarsData

  const programmesData = programmeHighlightsData?.data && programmeHighlightsData.data.length > 0
    ? { data: { data: programmeHighlightsData.data } }
    : allProgrammesData

  const isStrategicPillarsLoading = isAllStrategicLoading
  const isProgrammesLoading = isAllProgrammesLoading

  // Helper function to parse HTML content and extract text
  const parseHTMLContent = (html: string | null | undefined): string => {
    if (!html) return ''
    if (typeof document !== 'undefined') {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      return tempDiv.textContent || tempDiv.innerText || ''
    }
    return html.replace(/<[^>]*>/g, '').trim()
  }

  // Transform strategic pillars data
  const strategicPriorities = useMemo(() => {
    const pillars = strategicPillarsData?.data?.data || []
    return pillars
      .filter((pillar) => pillar.datePublished !== null)
      .slice(0, 4)
      .map((pillar) => {
        const textContent = parseHTMLContent(pillar.content)
        const description = textContent
          ? textContent.substring(0, 150) + (textContent.length > 150 ? '...' : '')
          : t('strategic_pillar_description')

        return {
          title: pillar.title,
          description,
          image: pillar.coverImage || defaultImage,
          route: `/programmes/strategic-pillars/${pillar.strategicPillarId}`
        }
      })
  }, [strategicPillarsData, defaultImage, t])

  // Transform programmes data
  const programmesPriorities = useMemo(() => {
    const programmes = programmesData?.data?.data || []
    return programmes
      .filter((prog) => prog.datePublished !== null && prog.landingPageAfsaProgramme)
      .slice(0, 4)
      .map((prog) => {
        const textContent = parseHTMLContent(prog.content)
        const description = textContent
          ? textContent.substring(0, 150) + (textContent.length > 150 ? '...' : '')
          : t('programme_description')

        return {
          title: prog.title,
          description,
          image: prog.coverImageUrl || defaultImage,
          route: `/programmes/${prog.programmeId}`
        }
      })
  }, [programmesData, defaultImage, t])

  const currentPriorities = activeTab === 'strategic' ? strategicPriorities : programmesPriorities
  const isLoading = activeTab === 'strategic' ? isStrategicPillarsLoading : isProgrammesLoading

  const handleCardClick = (route: string) => {
    navigate(route)
  }

  const handleViewAllClick = () => {
    if (activeTab === 'strategic') {
      navigate('/programmes/strategic-pillars')
    } else {
      navigate('/programmes/programs')
    }
  }

  return (
    <section className={`py-16 bg-white ${className}`} aria-labelledby="priorities-title">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className=" mb-5">

          <div className='pb-8 md:hidden flex justify-between'>
            <div>
              <div className="flex items-center">
                <button
                  onClick={() => setActiveTab('strategic')}
                  className={`py-2 text-sm font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'strategic'
                      ? 'text-gray-100 border-wine-100 '
                      : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                    }`}
                >
                  {t('strategic_pillars')}
                </button>
                <button
                  onClick={() => setActiveTab('programmes')}
                  className={`px-4 py-2 text-base font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'programmes'
                      ? 'text-black border-wine-100 '
                      : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                    }`}
                >
                  {t('programmes')}
                </button>
              </div>
            </div>
            {activeTab === 'strategic' && (
              <div className=' md:hidden'>
                <ViewAllButton onClick={handleViewAllClick} />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mb-6">


            <Heading level={3}
              id="priorities-title"
              className=""
            >
              {/* {activeTab === 'strategic' ? t('priorities_of_afsa') : t('afsa_programme')} */}
              {activeTab === 'strategic' ? data?.HOME_PRIORITIES_OF_AFSA?.title ?? t('priorities_of_afsa') : data?.HOME_AFSA_PROGRAMME?.title ?? t('afsa_programme')}
            </Heading>


            <div className="hidden sm:flex items-center">
              <button
                onClick={() => setActiveTab('strategic')}
                className={`px-4 py-2 text-base font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'strategic'
                    ? 'text-black border-wine-100 '
                    : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                  }`}
              >
                {t('strategic_pillars')}
              </button>
              <button
                onClick={() => setActiveTab('programmes')}
                className={`px-4 py-2 text-base font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'programmes'
                    ? 'text-black border-wine-100 '
                    : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                  }`}
              >
                {t('programmes')}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="max-w-2xl lg:w-auto" dangerouslySetInnerHTML={parseHtmlString(activeTab === 'strategic'
              ? data?.HOME_PRIORITIES_OF_AFSA?.description ?? t('priorities_desc_strategic')
              : data?.HOME_AFSA_PROGRAMME?.description ?? t('priorities_desc_programmes')
            )} ></div>
            <div className='hidden md:block'>

              <ViewAllButton onClick={handleViewAllClick} />
            </div>
          </div>
        </div>

        {/* Priority Cards Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading...</p>
          </div>
        ) : currentPriorities.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No {activeTab === 'strategic' ? 'strategic pillars' : 'programmes'} available</p>
          </div>
        ) : (
          <>
            {activeTab === 'programmes' ? (
              <div className="block md:hidden">
                {/* Mobile horizontal scroll for programmes */}
                <motion.div
                  className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide"
                  variants={staggerContainer}
                  // initial="hidden"
                  // whileInView="visible"
                  // viewport={viewportReveal}
                >
                  {currentPriorities.map((priority, index) => (
                    <motion.div
                      key={`${activeTab}-${priority.route}-${index}`}
                      className="flex-none w-72 snap-start"
                      variants={fadeUp}
                    >
                      <PriorityCard
                        title={priority.title}
                        description={priority.description}
                        image={priority.image}
                        onClick={() => handleCardClick(priority.route)}
                      />
                    </motion.div>
                  ))}
                </motion.div>
                {/* View All button below cards for programmes mobile */}
                <div className="mt-6 flex justify-center">
                  <ViewAllButton onClick={handleViewAllClick} />
                </div>
              </div>
            ) : null}

            {/* Desktop grid */}
            <motion.div
              className={`${activeTab === 'programmes' ? 'hidden md:grid' : 'grid'} grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6`}
              variants={staggerContainer}
              // initial="hidden"
              // whileInView="visible"
              // viewport={viewportReveal}
            >
              {currentPriorities.map((priority, index) => (
                <motion.div key={`${activeTab}-${priority.route}-${index}`} variants={fadeUp}>
                  <PriorityCard
                    title={priority.title}
                    description={priority.description}
                    image={priority.image}
                    onClick={() => handleCardClick(priority.route)}
                  />
                </motion.div>
              ))}
            </motion.div>
          </>
        )}
      </div>
    </section>
  )
}

export default PrioritiesSection